/**
 * Admin Assessment Service
 * Handles assessment CRUD, performance analytics, and configuration management
 */

import adminApi from './adminApi';

/**
 * Assessment DTO for admin management
 */
export interface AdminAssessmentDTO {
    id?: number;
    title: string;
    description?: string;
    topicId: number;
    courseId?: number;
    createdBy?: number;
    creatorRole?: string;
    scheduledDate?: string;
    dueDate?: string;
    durationMinutes?: number;
    passingScore: number;
    maxAttempts: number;
    requiresWorkSubmission: boolean;
    allowReassessment: boolean;
    status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
    questionCount?: number;
    questions?: AssessmentQuestionDTO[];
    createdAt?: string;
    updatedAt?: string;
}

/**
 * Assessment Question DTO
 */
export interface AssessmentQuestionDTO {
    id?: number;
    assessmentId?: number;
    questionType: 'MULTIPLE_CHOICE' | 'TRUE_FALSE' | 'SHORT_ANSWER' | 'LONG_ANSWER' | 'FILE_UPLOAD';
    questionText: string;
    options?: string[]; // JSON array for MCQ options
    correctAnswer?: string;
    points: number;
    explanation?: string;
    orderIndex?: number;
}

/**
 * Assessment Performance Stats
 */
export interface AssessmentPerformanceStats {
    totalAttempts: number;
    uniqueStudents: number;
    passedAttempts: number;
    failedAttempts: number;
    passRate: number;
    averageScore: number;
    averageTimeMinutes: number;
    averageRating?: number;
    totalRatings?: number;
    // Time-based breakdown
    dailyAttempts?: TimeSeriesData[];
    weeklyAttempts?: TimeSeriesData[];
    monthlyAttempts?: TimeSeriesData[];
}

export interface TimeSeriesData {
    date: string;
    attempts: number;
    passed: number;
    failed: number;
    averageScore: number;
}

/**
 * Assessment Configuration DTO
 */
export interface AssessmentConfigDTO {
    id?: number;
    level: 'SYSTEM' | 'SUBJECT' | 'TOPIC';
    entityId: number;
    maxQuestions?: number;
    timeLimitMinutes?: number;
    minPassPercent?: number;
    maxAttempts?: number;
    requiresWorkSubmission?: boolean;
    allowReassessment?: boolean;
    reassessmentGateDays?: number;
    allowAiGeneration?: boolean;
    aiDifficultyLevel?: string;
    aiQuestionTypes?: string[];
}

export interface ResolvedAssessmentConfigDTO extends AssessmentConfigDTO {
    source: 'SYSTEM' | 'SUBJECT' | 'TOPIC';
    sourceId: number;
}

// ==================== ASSESSMENT CRUD ====================

/**
 * Get all assessments for a topic
 */
export const getAssessmentsByTopic = async (topicId: number): Promise<AdminAssessmentDTO[]> => {
    const response = await adminApi.get(`/api/admin/assessments/topic/${topicId}`);
    return response.data?.data || response.data || [];
};

/**
 * Get a single assessment by ID
 */
export const getAssessmentById = async (assessmentId: number): Promise<AdminAssessmentDTO> => {
    const response = await adminApi.get(`/api/admin/assessments/${assessmentId}`);
    return response.data?.data || response.data;
};

/**
 * Create a new assessment
 */
export const createAssessment = async (assessment: Partial<AdminAssessmentDTO>): Promise<AdminAssessmentDTO> => {
    const response = await adminApi.post('/api/admin/assessments', assessment);
    return response.data?.data || response.data;
};

/**
 * Update an existing assessment
 */
export const updateAssessment = async (assessmentId: number, assessment: Partial<AdminAssessmentDTO>): Promise<AdminAssessmentDTO> => {
    const response = await adminApi.put(`/api/admin/assessments/${assessmentId}`, assessment);
    return response.data?.data || response.data;
};

/**
 * Save (create or update) an assessment
 */
export const saveAssessment = async (topicId: number, assessment: Partial<AdminAssessmentDTO>): Promise<AdminAssessmentDTO> => {
    if (assessment.id) {
        return updateAssessment(assessment.id, assessment);
    } else {
        return createAssessment({ ...assessment, topicId });
    }
};

/**
 * Delete an assessment
 */
export const deleteAssessment = async (assessmentId: number): Promise<void> => {
    await adminApi.delete(`/api/admin/assessments/${assessmentId}`);
};

/**
 * Publish/Unpublish an assessment
 */
export const toggleAssessmentStatus = async (assessmentId: number, status: 'DRAFT' | 'PUBLISHED'): Promise<AdminAssessmentDTO> => {
    const response = await adminApi.patch(`/api/admin/assessments/${assessmentId}/status`, { status });
    return response.data?.data || response.data;
};

// ==================== PERFORMANCE ANALYTICS ====================

/**
 * Get performance stats for a topic's assessments
 */
export const getAssessmentPerformanceByTopic = async (
    topicId: number,
    startDate?: string,
    endDate?: string,
    period?: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY'
): Promise<AssessmentPerformanceStats> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (period) params.append('period', period);
    
    const response = await adminApi.get(`/api/admin/assessments/topic/${topicId}/performance?${params.toString()}`);
    return response.data?.data || response.data;
};

/**
 * Get performance stats for a single assessment
 */
export const getAssessmentPerformance = async (
    assessmentId: number,
    startDate?: string,
    endDate?: string
): Promise<AssessmentPerformanceStats> => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await adminApi.get(`/api/admin/assessments/${assessmentId}/performance?${params.toString()}`);
    return response.data?.data || response.data;
};

// ==================== CONFIGURATION ====================

/**
 * Get assessment configuration for a specific level/entity
 */
export const getAssessmentConfig = async (level: 'SUBJECT' | 'TOPIC', entityId: number): Promise<AssessmentConfigDTO> => {
    const response = await adminApi.get(`/api/admin/assessment-config/${level.toLowerCase()}/${entityId}`);
    return response.data?.data || response.data || {};
};

/**
 * Save assessment configuration
 */
export const saveAssessmentConfig = async (level: 'SUBJECT' | 'TOPIC', entityId: number, config: AssessmentConfigDTO): Promise<AssessmentConfigDTO> => {
    const response = await adminApi.put(`/api/admin/assessment-config/${level.toLowerCase()}/${entityId}`, config);
    return response.data?.data || response.data;
};

/**
 * Resolve effective assessment configuration (inheritance)
 */
export const resolveAssessmentConfig = async (
    topicId?: number,
    subjectId?: number
): Promise<ResolvedAssessmentConfigDTO> => {
    const params = new URLSearchParams();
    if (topicId) params.append('topicId', topicId.toString());
    if (subjectId) params.append('subjectId', subjectId.toString());
    
    const response = await adminApi.get(`/api/admin/assessment-config/resolve?${params.toString()}`);
    return response.data?.data || response.data;
};

/**
 * Delete/reset configuration to inherit from parent
 */
export const deleteAssessmentConfig = async (level: 'SUBJECT' | 'TOPIC', entityId: number): Promise<void> => {
    await adminApi.delete(`/api/admin/assessment-config/${level.toLowerCase()}/${entityId}`);
};

// ==================== AI GENERATION ====================

/**
 * Generate assessment questions using AI
 */
export const generateAssessmentQuestions = async (
    topicId: number,
    config: {
        questionCount: number;
        questionTypes: string[];
        difficultyLevel: string;
        additionalInstructions?: string;
    }
): Promise<AssessmentQuestionDTO[]> => {
    const response = await adminApi.post(`/api/admin/assessments/topic/${topicId}/generate-questions`, config);
    return response.data?.data || response.data;
};

export default {
    getAssessmentsByTopic,
    getAssessmentById,
    createAssessment,
    updateAssessment,
    saveAssessment,
    deleteAssessment,
    toggleAssessmentStatus,
    getAssessmentPerformanceByTopic,
    getAssessmentPerformance,
    getAssessmentConfig,
    saveAssessmentConfig,
    resolveAssessmentConfig,
    deleteAssessmentConfig,
    generateAssessmentQuestions,
};
